import * as _ from "lodash";
import * as React from "react";
import { EasingType } from "../../earth/core/accelerationCurve";
import { NumberTransition, Transition } from "../../earth/core/transition";
import { EarthEngine } from "../../earth/earthEngine";
import { Land } from "../../earth/model/land";
import { NumberFormatter } from "../core/numberFormatter";
import { KeyedMetrics, MetricKey } from "../model/keyedMetrics";

const CenterRadius = 38;
const PieChartRadius = 52;
const PieChartSize = PieChartRadius * 2;
const ScaleFactors = [1.0, 0.85, 0.7, 0.6, 0.5, 0.5, 0.425, 0.35, 0.275, 0.2];
const MaxRankingForFullMode = 4;

interface PieChartProps {
    posX: number;
    posY: number;
    opacity: number;
    ranking: number;
    metrics: KeyedMetrics;
    land: Land;
}

class PieChartAnimationState {
    metrics: Transition<KeyedMetrics>;
    scaleFactor: NumberTransition;
    fullModePercent: NumberTransition;

    constructor(metrics: KeyedMetrics, scaleFactor: number, fullModePercent: number) {
        this.metrics = new Transition(metrics, metrics, 0.3, EasingType.Linear, KeyedMetrics.interpolate);
        this.scaleFactor = new NumberTransition(scaleFactor, scaleFactor, 0.3, EasingType.Linear);
        this.fullModePercent = new NumberTransition(fullModePercent, fullModePercent, 0.3, EasingType.Linear);
    }

    getOrderedMetricKeys(): MetricKey[] {
        let keys = _.union(this.metrics.start.keys, this.metrics.target.keys);
        return _.sortBy(keys, it => -Math.max(this.metrics.start.getValueForKey(it), this.metrics.target.getValueForKey(it)));
    }

    update(deltaTime: number, metrics: KeyedMetrics, scaleFactor: number, fullModePercent: number) {
        if (!KeyedMetrics.isEqual(this.metrics.target, metrics)) {
            this.metrics.resetWithNewTarget(metrics);
        }
        this.metrics.updateWithElapsedTime(deltaTime);

        if (this.scaleFactor.target != scaleFactor) {
            this.scaleFactor.resetWithNewTarget(scaleFactor);
        }
        this.scaleFactor.updateWithElapsedTime(deltaTime);

        if (this.fullModePercent.target != fullModePercent) {
            this.fullModePercent.resetWithNewTarget(fullModePercent);
        }
        this.fullModePercent.updateWithElapsedTime(deltaTime);
    }
}

class PieChartDrawParams {
    constructor(public metrics: KeyedMetrics, public metricKeys: MetricKey[], public scaleFactor: number, public fullModePercent: number) {
    }

    static isEqual(a: PieChartDrawParams, b: PieChartDrawParams): boolean {
        return KeyedMetrics.isEqual(a.metrics, b.metrics)
            && _.isEqual(a.metricKeys, b.metricKeys)
            && a.scaleFactor == b.scaleFactor
            && a.fullModePercent == b.fullModePercent;
    }
}

export class PieChart extends React.Component<PieChartProps, {}> {
    private _pixelRatio = window.devicePixelRatio;
    private _canvas: HTMLCanvasElement | null = null;
    private _animationState: PieChartAnimationState | null = null;

    collide(posX: number, posY: number): boolean {
        let radius = (this.shouldDisplayFullModeForRanking(this.props.ranking) ? PieChartRadius : CenterRadius) * this.getScaleFactorForRanking(this.props.ranking);
        let x = posX - this.props.posX;
        let y = posY - this.props.posY;
        return (x * x + y * y <= radius * radius);
    }

    update(deltaTime: number) {
        if (this._canvas) {
            let drawParams = this.animate(deltaTime);
            this.drawOnCanvas(this._canvas, drawParams);
        }
    }

    render(): JSX.Element {
        return <div style={{
                position: "absolute",
                left: this.props.posX - PieChartRadius,
                top: this.props.posY - PieChartRadius,
                width: PieChartSize,
                height: PieChartSize,
                opacity: this.props.opacity}}>
            <canvas ref={ref => { this._canvas = ref; }} width={PieChartSize * this._pixelRatio} height={PieChartSize * this._pixelRatio} style={{width: PieChartSize, height: PieChartSize}} />
        </div>;
    }

    private animate(deltaTime: number): PieChartDrawParams {
        let targetMetrics = this.props.metrics;
        let targetScaleFactor = this.getScaleFactorForRanking(this.props.ranking);
        let targetFullModePercent = this.shouldDisplayFullModeForRanking(this.props.ranking) ? 1.0 : 0.0;

        if (!this._animationState) {
            this._animationState = new PieChartAnimationState(targetMetrics, targetScaleFactor, targetFullModePercent);
        }
        this._animationState.update(deltaTime, targetMetrics, targetScaleFactor, targetFullModePercent);

        return {
            metrics: this._animationState.metrics.currentValue,
            metricKeys: this._animationState.getOrderedMetricKeys(),
            scaleFactor: this._animationState.scaleFactor.currentValue,
            fullModePercent: this._animationState.fullModePercent.currentValue
        };
    }

    private drawOnCanvas(canvas: HTMLCanvasElement, params: PieChartDrawParams) {
        let ctx = canvas.getContext("2d")!;
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        ctx.save();
        ctx.scale(this._pixelRatio, this._pixelRatio);

        ctx.save();
        ctx.translate(PieChartRadius, PieChartRadius);
        ctx.scale(params.scaleFactor, params.scaleFactor);
        ctx.translate(-PieChartRadius, -PieChartRadius);

        // Draw the inner circle
        ctx.beginPath();
        ctx.fillStyle = "rgba(0, 0, 0, 0.3)";
        ctx.arc(PieChartRadius, PieChartRadius, CenterRadius, 0, 2 * Math.PI);
        ctx.fill();

        // Draw the outline of the inner circle
        ctx.beginPath();
        ctx.lineWidth = 6.0;
        ctx.strokeStyle = `rgba(255, 255, 255, ${0.7 * (1.0 - params.fullModePercent)})`;
        ctx.arc(PieChartRadius, PieChartRadius, CenterRadius + 3, 0, 2 * Math.PI);
        ctx.stroke();

        // Draw the total metrics-value
        let total = params.metrics.getTotalForKeys(params.metricKeys);
        ctx.font = "18px GothamBold";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillStyle = `rgba(210, 210, 210, ${params.fullModePercent})`;
        ctx.fillText(`${NumberFormatter.getShortValueToDisplay(total)}`, PieChartRadius, PieChartRadius);

        // Draw the pie-chart parts from the largest to the smallest brand
        if (params.fullModePercent > 0) {
            let curAngle = -Math.PI / 2;
            let innerRadius = CenterRadius;
            let outerRadius = innerRadius + params.fullModePercent * (PieChartRadius - innerRadius);
            for (let metricKey of params.metricKeys) {
                let value = params.metrics.getValueForKey(metricKey);
                let angle = 2 * Math.PI * value / total;

                ctx.beginPath();
                ctx.fillStyle = metricKey.color;
                ctx.arc(PieChartRadius, PieChartRadius, outerRadius, curAngle, curAngle + angle);
                ctx.arc(PieChartRadius, PieChartRadius, innerRadius, curAngle + angle, curAngle, true);
                ctx.closePath();
                ctx.fill();

                curAngle += angle;
            }
        }

        ctx.restore();
        ctx.restore();
    }

    private shouldDisplayFullModeForRanking(ranking: number): boolean {
        return ranking <= MaxRankingForFullMode;
    }

    private getScaleFactorForRanking(ranking: number): number {
        return ScaleFactors[Math.min(Math.max(ranking, 0), ScaleFactors.length - 1)];
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--1!./src/app/ui/pieChart.tsx